import React, { useRef, useState, useEffect } from 'react';
import {
    Text,
    Box,
    Tooltip,
    Stack,
    Group,
    Progress,
    Tabs,
    ActionIcon,
} from '@mantine/core';
import { Card } from '@xplatform/design-system';
import {
    IconCloudUpload,
    IconFile,
    IconSettings,
    IconFilter,
    IconRefresh,
} from '@tabler/icons-react';
import { ProcessedResultsList, ExtractionOption, FilterPanel } from '@features/index';
import { useDoctypes, useExtraction } from '@src/hooks/useContext';
import { useAuth } from 'tecrmi_components/useAuth';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import './UploadSection.scss';
import type { IProcessedResultsFilterViewModel } from '@src/models';
import { useSelectedOrganisation } from 'xplatform_shell/useSelectedOrganisation';
import { useTranslation } from 'react-i18next';
import SharePointFolderPicker from './SharePointFolderPicker';
import type { SharePointSelection } from '@src/services/sharepointApi';

const getInitialFilter = (userEmail?: string, userId?: string): IProcessedResultsFilterViewModel => {
    return {
        startDate: null,
        endDate: null,
        fileName: null,
        userId: userId,
        userEmail: userEmail,
        processId: null,
    };
};

interface UploadSectionProps {
    uploadedFile?: File;
    selectedSharePointFolder?: SharePointSelection;
    onFolderSelect: (folder: SharePointSelection | undefined) => void;
    extractionOptions: ExtractionOptions;
    onFileUpload: (file: File) => void;
    onOptionsChange: (options: ExtractionOptions) => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
    uploadedFile,
    selectedSharePointFolder,
    onFolderSelect,
    extractionOptions,
    onFileUpload,
    onOptionsChange,
}) => {
    const { t } = useTranslation();
    const { isLoading: isDoctypesLoading, doctypes } = useDoctypes();
    const { user } = useAuth();
    const { organisation } = useSelectedOrganisation();
    const {
        doctypeCode,
        isLoadingResults,
        isExtracting,
        uploadProgress,
        getProcessedResultsInfo,
        showSuccess,
    } = useExtraction();

    const [isPopoverOpen, setIsDrawerOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSharePointOpen, setIsSharePointOpen] = useState(false);
    const [uploadSource, setUploadSource] = useState<'file' | 'sharepoint'>('file');
    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [currentFilter, setCurrentFilter] = useState<IProcessedResultsFilterViewModel>(getInitialFilter(user?.email, organisation?.userId));

    useEffect(() => {
        setIsDrawerOpen(false);
        const initialFilter = getInitialFilter(user?.email, organisation?.userId);
        setCurrentFilter(initialFilter);
        if (doctypeCode) {
            getProcessedResultsInfo(doctypeCode, initialFilter);
        }
    }, [doctypeCode, getProcessedResultsInfo, user, organisation]);

    const hasDoctype = !!doctypeCode;
    const selectedDoctype = doctypes.find((dt) => dt.docTypeCode === doctypeCode);
    const sharePointEnabled = selectedDoctype?.sharepointEnabled ?? false;
    const mappingAvailable = Boolean(selectedDoctype?.mappingConfig?.enabled);

    useEffect(() => {
        if (!sharePointEnabled) {
            setUploadSource('file');
            setIsSharePointOpen(false);
        }
    }, [sharePointEnabled]);

    useEffect(() => {
        setUploadSource('file');
        setIsSharePointOpen(false);
    }, [doctypeCode]);

    const handleFileUpload = async (file: File) => {
        onFileUpload(file);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) handleFileUpload(file);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (uploadSource !== 'file') return;
        const file = event.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const openFileDialog = () => {
        if (!hasDoctype || isDoctypesLoading || isExtracting) return;
        fileInputRef.current?.click();
    };

    const handleOpenFilterPanel = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        setIsFilterOpen(true);
    };

    const handleCloseFilterPanel = () => {
        setIsFilterOpen(false);
        setFilterAnchorEl(null);
    };

    const handleApplyFilter = (filter: IProcessedResultsFilterViewModel) => {
        if (!doctypeCode) return;
        setCurrentFilter(filter);
        getProcessedResultsInfo(doctypeCode, filter);
    };

    const handleRefresh = () => {
        if (!doctypeCode) return;
        getProcessedResultsInfo(doctypeCode, currentFilter);
    };

    const handleOpenPopover = () => {
        setIsDrawerOpen(true);
    };

    const handleClosePopover = () => {
        setIsDrawerOpen(false);
    };

    const handleSaveOptions = (options: ExtractionOptions) => {
        onOptionsChange(options);
    };

    const uploadDisabled = !hasDoctype || isDoctypesLoading;

    return (
        <Card className="upload-section">
            <Stack p="xs" gap="md">
                {sharePointEnabled && (
                    <Tabs
                        value={uploadSource}
                        onChange={(value) => {
                            if (value !== 'file' && value !== 'sharepoint') return;
                            setUploadSource(value);
                        }}
                        className="upload-source-tabs"
                    >
                        <Tabs.List grow>
                            <Tabs.Tab value="file">Browse file</Tabs.Tab>
                            <Tabs.Tab value="sharepoint">{t('extraction.sharePoint.label')}</Tabs.Tab>
                        </Tabs.List>
                    </Tabs>
                )}

                <Box
                    className={`upload-dropzone ${uploadDisabled ? 'disabled' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={uploadSource === 'sharepoint'
                        ? () => { if (!uploadDisabled && !isExtracting) setIsSharePointOpen(true); }
                        : openFileDialog}
                    role="button"
                    tabIndex={uploadDisabled ? -1 : 0}
                    onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && !uploadDisabled && !isExtracting) {
                            event.preventDefault();
                            if (uploadSource === 'sharepoint') setIsSharePointOpen(true);
                            else openFileDialog();
                        }
                    }}
                >
                    <IconCloudUpload size={32} className={`upload-icon ${uploadDisabled ? 'disabled' : ''}`} />
                    <Text size="sm" mb="xs" c={uploadDisabled ? 'dimmed' : undefined}>
                        {uploadSource === 'sharepoint' ? t('extraction.sharePoint.browse') : 'Drop files here or click to upload'}
                    </Text>
                    {!uploadDisabled && (
                        <Text size="xs" c="dimmed" mb="md">
                            {uploadSource === 'sharepoint'
                                ? t('extraction.sharePoint.description')
                                : 'Supported formats: PDF, JPG, JPEG, PNG, HEIC, ZIP'}
                        </Text>
                    )}
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.zip"
                    onChange={handleFileSelect}
                    disabled={uploadDisabled}
                    style={{ display: 'none' }}
                />

                {(uploadedFile || selectedSharePointFolder) && (
                    <Stack gap={6} className="uploaded-file-info" p="xs">
                        <Group justify="space-between" gap="xs" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                <IconFile size={18} className="icon-primary" style={{ flexShrink: 0 }} />
                                <Text size="sm" truncate="end">
                                    {uploadedFile?.name ?? selectedSharePointFolder?.name}
                                </Text>
                            </Group>
                            <IconSettings
                                size={20}
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={handleOpenPopover}
                            />
                        </Group>
                        {isExtracting && (
                            <Stack gap={2}>
                                <Progress
                                    value={uploadProgress}
                                    size="sm"
                                    radius="xl"
                                    animated={uploadProgress < 100}
                                    striped={uploadProgress < 100}
                                />
                                <Text size="xs" c="dimmed" ta="right">
                                    {uploadProgress < 100
                                        ? `Uploading ${uploadProgress}%`
                                        : 'Upload complete'}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}

                {hasDoctype && (
                    <>
                        <Group justify="space-between" gap="xs" wrap="nowrap" className="processed-results-toolbar">
                            <Text className="processed-results-title">
                                {t('extraction.processedResults.title')}
                            </Text>
                            <Group gap={6} wrap="nowrap">
                                <Tooltip label={t('extraction.processedResults.refresh')} position="top">
                                    <ActionIcon
                                        variant="subtle"
                                        className="processed-results-toolbar-button"
                                        aria-label={t('extraction.processedResults.refresh')}
                                        disabled={isLoadingResults}
                                        onClick={handleRefresh}
                                    >
                                        <IconRefresh size={18} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label={t('extraction.processedResults.filter')} position="top">
                                    <ActionIcon
                                        variant="subtle"
                                        className="processed-results-toolbar-button"
                                        aria-label={t('extraction.processedResults.filter')}
                                        onClick={handleOpenFilterPanel}
                                    >
                                        <IconFilter size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Group>
                    </>
                )}

                {hasDoctype && (
                    <ProcessedResultsList mappingAvailable={mappingAvailable} />
                )}
            </Stack>

            <FilterPanel
                anchorEl={filterAnchorEl}
                open={isFilterOpen}
                onClose={handleCloseFilterPanel}
                onApplyFilter={handleApplyFilter}
                initialFilter={currentFilter}
            />

            <ExtractionOption
                open={isPopoverOpen}
                onClose={handleClosePopover}
                onSave={handleSaveOptions}
                initialOptions={extractionOptions}
                uploadedFile={uploadedFile}
                doctypeCode={doctypeCode}
            />

            <SharePointFolderPicker
                opened={isSharePointOpen && sharePointEnabled}
                doctypeCode={doctypeCode}
                disabled={uploadDisabled || isExtracting}
                onClose={() => setIsSharePointOpen(false)}
                onFolderReady={(folder) => {
                    onFolderSelect(folder);
                    showSuccess(t('extraction.sharePoint.success', { name: folder.name }));
                }}
            />
        </Card>
    );
};

export default UploadSection;
